import dynamic from 'next/dynamic';
import { JSX, SVGProps } from 'react';

import { cn } from '@natu/utils/cn';

import {
  getAssetFromStoryblok,
  resolveStoryblokStyles,
  sbEditable,
  type SBProps,
  type Spacing,
  type StoryblokAsset,
  type StoryblokLink,
} from '../../../../utils';
import { CaseStudyCardButton } from './CaseStudyCardButton';

const ResponsiveImage = dynamic(() =>
  import('@natu/ui/ResponsiveImage').then(mod => mod.ResponsiveImage),
);

interface SBCaseStudyCardProps {
  stretch?: boolean;
  evenStretch?: boolean;
  companyName?: string;
  projectCategory?: string;
  title?: string;
  description?: string;
  ratingValue?: string;
  ratingLabel?: string;
  firstStatisticFirstValue?: string;
  firstStatisticSecondValue?: string;
  secondStatisticFirstValue?: string;
  secondStatisticSecondValue?: string;
  caseStudyUrl?: StoryblokLink;
  image?: StoryblokAsset;
  mbMobile?: Spacing;
  mbTablet?: Spacing;
  mbDesktop?: Spacing;
  mtMobile?: Spacing;
  mtTablet?: Spacing;
  mtDesktop?: Spacing;
}

const CaseStudyCardStar = (props: JSX.IntrinsicAttributes & SVGProps<SVGSVGElement>) => (
  <svg width={22} height={21} viewBox="0 0 22 21" fill="none" {...props}>
    <path
      d="M10.5383 0.710061C10.7091 0.299415 11.2909 0.299416 11.4617 0.710062L14.0568 6.94943C14.1288 7.12254 14.2916 7.24083 14.4785 7.25581L21.2144 7.79583C21.6577 7.83137 21.8375 8.38462 21.4997 8.67396L16.3676 13.0701C16.2252 13.1921 16.1631 13.3835 16.2066 13.5659L17.7745 20.139C17.8777 20.5716 17.4071 20.9135 17.0275 20.6817L11.2606 17.1593C11.1006 17.0616 10.8994 17.0616 10.7394 17.1593L4.97249 20.6817C4.59294 20.9135 4.12231 20.5716 4.22551 20.139L5.79343 13.5659C5.83694 13.3835 5.77475 13.1921 5.63236 13.0701L0.5003 8.67396C0.162531 8.38462 0.342295 7.83137 0.785622 7.79583L7.52154 7.25581C7.70843 7.24083 7.87124 7.12254 7.94324 6.94943L10.5383 0.710061Z"
      fill="#FDB022"
    />
  </svg>
);

export const SBCaseStudyCard = ({ blok }: SBProps<SBCaseStudyCardProps>) => {
  const {
    caseStudyUrl,
    companyName,
    description,
    evenStretch,
    firstStatisticFirstValue,
    firstStatisticSecondValue,
    image,
    mbDesktop,
    mbMobile,
    mbTablet,
    mtDesktop,
    mtMobile,
    mtTablet,
    projectCategory,
    ratingLabel,
    ratingValue,
    secondStatisticFirstValue,
    secondStatisticSecondValue,
    stretch,
    title,
  } = blok;

  const className = resolveStoryblokStyles({
    mb: mbMobile,
    mbTablet,
    mbDesktop,
    mt: mtMobile,
    mtTablet,
    mtDesktop,
  });

  const hideGrid =
    !firstStatisticFirstValue &&
    !firstStatisticSecondValue &&
    !ratingLabel &&
    !ratingValue &&
    !secondStatisticFirstValue &&
    !secondStatisticSecondValue;

  const resolvedImage = getAssetFromStoryblok(image);

  return (
    <div
      className={cn(
        className,
        'border-naturaily-azureGray h-fit rounded-2xl border',
        stretch && 'lg:grid lg:grid-cols-[520px_auto]',
        stretch && evenStretch && 'lg:grid lg:grid-cols-2',
      )}
      {...sbEditable(blok)}
    >
      <div
        className={cn(
          'bg-naturaily-azureGray rounded-t-2xl p-6 lg:p-8',
          stretch &&
            'lg:rounded-bl-2xl lg:rounded-tl-2xl lg:rounded-tr-none lg:py-10 lg:pl-8 lg:pr-20',
          stretch && evenStretch && 'p-6 lg:p-8',
        )}
      >
        {(projectCategory || companyName) && (
          <div
            className={cn(
              'mb-4 flex flex-col gap-y-1 lg:flex-row lg:flex-wrap lg:gap-x-3',
              stretch && !evenStretch ? 'lg:mb-14' : 'lg:mb-2',
            )}
          >
            <p className="text-primary-700 text-sm font-semibold -tracking-[0.4%]">{companyName}</p>
            <p className="text-sm font-semibold -tracking-[0.4%] text-gray-400">
              {projectCategory}
            </p>
          </div>
        )}
        {title && (
          <p
            className={cn(
              'text-xl font-semibold -tracking-[0.2px] text-gray-900',
              stretch ? 'xl:text-display-md-34 mb-6' : 'mb-2 xl:text-2xl',
            )}
          >
            {title}
          </p>
        )}
        {description && (
          <p className="mb-6 text-sm text-gray-500 lg:mb-8 xl:text-base">{description}</p>
        )}
        {!hideGrid && (
          <div className="mb-8 grid grid-cols-3 gap-x-6">
            {ratingValue && (
              <div className="flex gap-x-2">
                <CaseStudyCardStar className="shrink-0" />
                <div className="flex flex-col gap-y-0.5">
                  <p className="text-base font-bold -tracking-[0.2px] text-gray-900 xl:text-lg">
                    {ratingValue}
                  </p>
                  <p className="text-xs -tracking-[0.4%] text-gray-400 xl:text-sm">{ratingLabel}</p>
                </div>
              </div>
            )}
            {(firstStatisticFirstValue || firstStatisticSecondValue) && (
              <div className="flex flex-col gap-y-0.5">
                <p className="text-base font-bold -tracking-[0.2px] text-gray-900 xl:text-lg">
                  {firstStatisticFirstValue}
                </p>
                <p className="text-xs -tracking-[0.4%] text-gray-400 xl:text-sm">
                  {firstStatisticSecondValue}
                </p>
              </div>
            )}
            {(secondStatisticFirstValue || secondStatisticSecondValue) && (
              <div className="flex flex-col gap-y-0.5">
                <p className="text-base font-bold -tracking-[0.2px] text-gray-900 xl:text-lg">
                  {secondStatisticFirstValue}
                </p>
                <p className="text-xs -tracking-[0.4%] text-gray-400 xl:text-sm">
                  {secondStatisticSecondValue}
                </p>
              </div>
            )}
          </div>
        )}
        <CaseStudyCardButton link={caseStudyUrl} />
      </div>
      {resolvedImage.src && (
        <ResponsiveImage
          {...resolvedImage}
          className={cn(
            'h-full rounded-b-2xl object-cover',
            !stretch && 'aspect-video',
            stretch && 'max-h-none lg:rounded-b-none lg:rounded-br-2xl lg:rounded-tr-2xl',
          )}
        />
      )}
    </div>
  );
};
